import * as React from 'react';
import {
    ChangeEvent,
    ReactElement,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslate } from 'react-admin';
import { PopoverOrigin, Popover, SxProps } from '@mui/material';
import debounce from 'lodash/debounce';

import { useSearch } from './dataProvider';
import { SearchInput } from './SearchInput';
import {
    SearchInputColor,
    SearchPanelProps,
    SearchRequestOptions,
} from './types';
import { SearchResultsPanel } from './SearchResultsPanel';
import { SearchResultContextProvider } from './SearchResultContext';
import { SearchHistoryPanel } from './SearchHistoryPanel';

/**
 * A component which displays an input, calls the dataProvider `search` method
 * when the input changes and shows the search results inside a Material UI
 * [`<PopOver>`](https://mui.com/components/popover/).
 *
 * By default, it will group the search result items by resource and show their
 * `content.label` and `content.description`.
 *
 * @param props {SearchProps}
 * @param props.children {ReactElement} A React Element which will be rendered inside the Material UI `<Popover>`
 * @param {'light' | 'dark'} props.color The color mode for the search input, applying light or dark backgrounds
 *
 * @example <caption>Here's how to include it inside a custom `<AppBar>` component</caption>
 * import { Admin, AppBar, Layout, Resource } from 'react-admin';
 * import { Typography } from '@mui/material';
 * import { Search } from '@react-admin/ra-search';
 *
 * const MyAppbar = (props) => (
 *     <AppBar {...props}>
 *         <Typography
 *             variant="h6"
 *             color="inherit"
 *             sx={{
 *                 flex: 1,
 *                 textOverflow: 'ellipsis',
 *                 whiteSpace: 'nowrap',
 *                 overflow: 'hidden',
 *             }}
 *             id="react-admin-title"
 *         />
 *         <Search />
 *     </AppBar>
 * );
 *
 *
 * const MyLayout = (props) => (
 *     <Layout {...props} appBar={MyAppbar} />
 * );
 *
 * export const App = () => (
 *     <Admin dataProvider={searchDataProvider} layout={MyLayout}>
 *         {...}
 *     </Admin>
 * );
 *
 * @example <caption>Customizing the <Popover> Content</caption>
 * import { Admin, AppBar, Layout, Resource } from 'react-admin';
 * import { Typography } from '@mui/material';
 * import { Search } from '@react-admin/ra-search';
 *
 * const MySearchResultsPanel = () => {
 *     const { data, onClose } = useSearchResults();
 *
 *     return (
 *         <ul>
 *             {data.map(item => (
 *                 <li key={item.id}>
 *                     <Link to={item.url} onClick={onClose}>
 *                         <strong>{item.content.label}</strong>
 *                     </Link>
 *                     <p>{item.content.description}</p>
 *                 </li>
 *             ))}
 *         </ul>
 *     );
 * };
 *
 * const MyAppbar = (props) => (
 *     <AppBar {...props}>
 *         <Typography
 *             variant="h6"
 *             color="inherit"
 *             sx={{
 *                 flex: 1,
 *                 textOverflow: 'ellipsis',
 *                 whiteSpace: 'nowrap',
 *                 overflow: 'hidden',
 *             }}
 *             id="react-admin-title"
 *         />
 *         <Search>
 *             <CustomSearchResultsPanel />
 *         </Search>
 *     </AppBar>
 * );
 *
 * const MyLayout = (props) => (
 *     <Layout {...props} appBar={MyAppbar} />
 * );
 *
 * export const App = () => (
 *     <Admin
 *         dataProvider={searchDataProvider}
 *         layout={MyLayout}
 *     >
 *         {...}
 *     </Admin>
 * );
 *
 * @example <caption>Customizing the <SearchResultItem> Component</caption>
 * import { Admin, AppBar, Layout, Resource } from 'react-admin';
 * import { Typography } from '@mui/material';
 * import { Search, SearchResultsPanel, SearchResultItem } from '@react-admin/ra-search';
 *
 * const MyAppbar = (props) => (
 *     <AppBar {...props}>
 *         <Typography
 *             variant="h6"
 *             color="inherit"
 *             sx={{
 *                 flex: 1,
 *                 textOverflow: 'ellipsis',
 *                 whiteSpace: 'nowrap',
 *                 overflow: 'hidden',
 *             }}
 *             id="react-admin-title"
 *         />
 *         <Search>
 *             <SearchResultsPanel>
 *                 <SearchResultItem
 *                     label={(record) => (
 *                         <>
 *                             {record.type === 'artists' ? (
 *                                 <PersonIcon />
 *                             ) : (
 *                                 <MusicIcon />
 *                             )}
 *                             <span>{record.content.label}</span>
 *                         </>
 *                     )}
 *                 />
 *             </SearchResultsPanel>
 *         </Search>
 *     </AppBar>
 * );
 *
 * const MyLayout = (props) => (
 *     <Layout {...props} appBar={MyAppbar} />
 * );
 *
 * export const App = () => (
 *     <Admin dataProvider={dataProvider} layout={MyLayout}>
 *         {...}
 *     </Admin>
 * );
 */
export const Search = (props: SearchProps): ReactElement => {
    const {
        children = <SearchResultsPanel />,
        color = 'light',
        options,
        wait = 500,
        historySize = 5,
        sx,
    } = props;
    const [history, setHistory] = useState<string[]>([]);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [search, state] = useSearch(undefined, {
        ...options,
        onSuccess: () => {
            setOpen(true);
        },
    });
    const debouncedSearch = useMemo(
        () => debounce(search, wait),
        [search, wait]
    );
    const ref = useRef();
    const inputRef = useRef();
    const translate = useTranslate();

    const handleClose = useCallback(
        (addQueryToHistory = true): void => {
            setOpen(false);
            if (addQueryToHistory && query && !history.includes(query)) {
                const newHistory = [query, ...history];
                newHistory.splice(
                    historySize,
                    newHistory.length > historySize
                        ? newHistory.length - historySize
                        : 0
                );
                setHistory(newHistory);
            }
        },
        [query, history, historySize]
    );

    const runSearch = useCallback(
        (query): void => {
            setQuery(query);
            debouncedSearch(query);
        },
        [setQuery, debouncedSearch]
    );

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            const query = event.target
                ? event.target.value
                : (event as unknown as string);
            runSearch(query);
            if (!query && history.length === 0) {
                handleClose(false);
            }
        },
        [handleClose, history.length, runSearch]
    );

    const handleFocus = useCallback((): void => {
        if ((query && state.isLoaded) || history.length > 0) {
            setOpen(true);
        }
    }, [query, state, history]);

    const contextValue = useMemo(
        () => ({
            ...state,
            onClose: handleClose,
        }),
        [state, handleClose]
    );

    return (
        <>
            <SearchInput
                color={color}
                ref={ref}
                inputRef={inputRef}
                onChange={handleChange}
                onFocus={handleFocus}
                value={query}
                sx={sx}
            />
            <Popover
                anchorEl={ref.current}
                anchorReference="anchorEl"
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                elevation={4}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                disablePortal
                open={open}
                onClose={handleClose}
            >
                {query ? (
                    state.error ? (
                        translate('ra.notification.http_error', {
                            _: 'Server communication error',
                        })
                    ) : (
                        <SearchResultContextProvider value={contextValue}>
                            {children}
                        </SearchResultContextProvider>
                    )
                ) : history.length > 0 ? (
                    <SearchHistoryPanel
                        history={history}
                        onSelect={runSearch}
                    />
                ) : null}
            </Popover>
        </>
    );
};

const anchorOrigin: PopoverOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
};

const transformOrigin: PopoverOrigin = {
    vertical: 'top',
    horizontal: 'right',
};

export interface SearchProps {
    children?: ReactElement<SearchPanelProps>;
    color?: SearchInputColor;
    historySize?: number;
    options?: SearchRequestOptions;
    wait?: number; // debounce delay in milliseconds
    sx?: SxProps;
}
